import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import {
  LayoutTemplate,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Loader2,
  ArrowLeft,
  Save,
  PenTool,
  Calendar,
  Type,
  GripVertical
} from "lucide-react";
import { toast } from "sonner";

const FIELD_TYPES = [
  { id: "signature", label: "Signature", icon: PenTool, color: "bg-blue-100 text-blue-700" },
  { id: "date", label: "Date", icon: Calendar, color: "bg-green-100 text-green-700" },
  { id: "text", label: "Texte", icon: Type, color: "bg-purple-100 text-purple-700" }
];

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    fields: []
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get("/templates");
      setTemplates(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des templates");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", fields: [] });
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error("Le nom est requis");
      return;
    }
    if (formData.fields.length === 0) {
      toast.error("Ajoutez au moins un champ");
      return;
    }

    setSaving(true);
    try {
      await api.post("/templates", formData);
      toast.success("Template créé");
      setShowCreateDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name) {
      toast.error("Le nom est requis");
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/templates/${selectedTemplate.id}`, formData);
      toast.success("Template mis à jour");
      setShowEditDialog(false);
      setSelectedTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTemplate) return;
    try {
      await api.delete(`/templates/${deleteTemplate.id}`);
      toast.success("Template supprimé");
      setDeleteTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      fields: template.fields
    });
    setShowEditDialog(true);
  };

  const duplicateTemplate = async (template) => {
    try {
      await api.post("/templates", {
        name: `${template.name} (copie)`,
        description: template.description,
        fields: template.fields
      });
      toast.success("Template dupliqué");
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la duplication");
    }
  };

  const addField = (type) => {
    const newField = {
      id: `field-${Date.now()}`,
      type: type,
      label: FIELD_TYPES.find(t => t.id === type)?.label || "Champ",
      page: 1,
      x: 100,
      y: 100 + formData.fields.length * 80,
      width: type === "signature" ? 200 : 150,
      height: type === "signature" ? 50 : 30
    };
    setFormData({ ...formData, fields: [...formData.fields, newField] });
  };

  const removeField = (fieldId) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter(f => f.id !== fieldId)
    });
  };

  const updateField = (fieldId, updates) => {
    setFormData({
      ...formData,
      fields: formData.fields.map(f =>
        f.id === fieldId ? { ...f, ...updates } : f
      )
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Templates de signature</h1>
          <p className="text-muted-foreground mt-1">
            Créez des modèles réutilisables avec des champs prédéfinis
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
          className="btn-active"
          data-testid="create-template-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau template
        </Button>
      </div>

      {/* Templates List */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <LayoutTemplate className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg text-muted-foreground">Aucun template</p>
            <p className="text-sm text-muted-foreground mt-1">
              Créez votre premier template pour accélérer vos demandes de signature
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="card-hover">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <LayoutTemplate className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {template.fields?.length || 0} champ(s)
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`template-menu-${template.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(template)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateTemplate(template)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Dupliquer
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setDeleteTemplate(template)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {template.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {template.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {template.fields?.slice(0, 4).map((field) => {
                    const fieldType = FIELD_TYPES.find(t => t.id === field.type);
                    return (
                      <Badge
                        key={field.id}
                        variant="secondary"
                        className={fieldType?.color || ""}
                      >
                        {field.label || fieldType?.label}
                      </Badge>
                    );
                  })}
                  {template.fields?.length > 4 && (
                    <Badge variant="outline">+{template.fields.length - 4}</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Par {template.created_by_name}</span>
                  <span>{formatDate(template.created_at)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={showCreateDialog || showEditDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setShowEditDialog(false);
            setSelectedTemplate(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? "Modifier le template" : "Nouveau template"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="template-name">Nom du template *</Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Contrat standard"
                data-testid="template-name-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description optionnelle..."
                rows={2}
              />
            </div>

            {/* Fields */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Champs de signature</Label>
                <div className="flex gap-2">
                  {FIELD_TYPES.map((type) => (
                    <Button
                      key={type.id}
                      variant="outline"
                      size="sm"
                      onClick={() => addField(type.id)}
                      data-testid={`add-field-${type.id}`}
                    >
                      <type.icon className="w-4 h-4 mr-1" />
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              {formData.fields.length === 0 ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
                  <GripVertical className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Ajoutez des champs en cliquant sur les boutons ci-dessus</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.fields.map((field, index) => {
                    const fieldType = FIELD_TYPES.find(t => t.id === field.type);
                    return (
                      <div
                        key={field.id}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50"
                      >
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${fieldType?.color}`}>
                          {fieldType && <fieldType.icon className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 grid grid-cols-4 gap-2">
                          <Input
                            value={field.label}
                            onChange={(e) => updateField(field.id, { label: e.target.value })}
                            placeholder="Label"
                            className="col-span-2"
                          />
                          <Input
                            type="number"
                            value={field.x}
                            onChange={(e) => updateField(field.id, { x: parseInt(e.target.value) || 0 })}
                            placeholder="X"
                          />
                          <Input
                            type="number"
                            value={field.y}
                            onChange={(e) => updateField(field.id, { y: parseInt(e.target.value) || 0 })}
                            placeholder="Y"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeField(field.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
                resetForm();
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={showEditDialog ? handleUpdate : handleCreate}
              disabled={saving}
              data-testid="save-template-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {showEditDialog ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le template ?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Cette action est irréversible. Le template "{deleteTemplate?.name}" sera
            définitivement supprimé.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplate(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
